import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { map, takeUntil, withLatestFrom, startWith } from 'rxjs/operators';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { MatDialogRef } from '@angular/material/dialog';
import { GenesysCloudQueue } from 'common/store/genesys-cloud/queues/models/genesys-cloud-queue.model';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { createEmailConversation, createEmailConversationFailure, createEmailConversationSuccess } from 'common/store/genesys-cloud/conversations/emails/actions/email-conversation.actions';
import { MatFormField } from '@angular/material/form-field';
import { GenesysProactiveEmailModalStore } from './genesys-proactive-email-modal.store';
import { queueRequiredValidator } from './validators/queue-required.validator';

@Component({
  selector: 'app-genesys-proactive-email-modal',
  templateUrl: './genesys-proactive-email-modal.component.html',
  styleUrls: ['./genesys-proactive-email-modal.component.scss'],
  providers: [GenesysProactiveEmailModalStore],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenesysProactiveEmailModalComponent implements OnInit, OnDestroy, AfterViewInit {
  // Workaround for outline text & prefix not being aligned, see: https://github.com/angular/components/issues/15027
  @ViewChildren(MatFormField) formFields: QueryList<MatFormField>;
  ngAfterViewInit(): void {
    setTimeout(() => this.formFields.forEach(ff => ff.updateOutlineGap()), 100);
  }

  readonly separatorKeyCodes = [ENTER, COMMA, SPACE];
  private static readonly emailRegex = /\S+@\S+\.\S+/;

  queue = new UntypedFormControl('', [
    queueRequiredValidator()
  ]);

  recipientsInput = new UntypedFormControl('', []);

  recipients = new UntypedFormControl([], [
    Validators.required
  ]);

  proactiveEmailForm = this.formBuilder.group({
    queue: this.queue,
    recipients: this.recipients,
    recipientsInput: this.recipientsInput
  });

  private readonly onDestroySubject = new Subject<void>();

  readonly queues$: Observable<GenesysCloudQueue[]> = this.modalStore.queues$;
  readonly loading$: Observable<boolean> = this.modalStore.loading$;
  readonly filteredQueues$: Observable<GenesysCloudQueue[]> = this.proactiveEmailForm.controls.queue.valueChanges.pipe(
    withLatestFrom(this.queues$),
    map(([ filterValue, queues ]) => {
      const queueName = (filterValue?.name || filterValue || '').toLowerCase();

      return queueName ? queues.filter(q => q.name.toLowerCase().includes(queueName)) : queues;
    })
  );

  readonly createConversationFailed$: Observable<boolean> = this.modalStore.createConversationFailed$;

  private readonly statusInvalid$ = this.proactiveEmailForm.statusChanges.pipe(
    map(formStatus => formStatus === 'INVALID'),
    startWith(true)
  );

  readonly isSubmitButtonDisabled$: Observable<boolean> = combineLatest([this.loading$, this.statusInvalid$]).pipe(
    map(([loading, formInvalid])=>
      loading || formInvalid
    )
  );

  constructor(public dialogRef: MatDialogRef<GenesysProactiveEmailModalComponent>,
              private readonly modalStore: GenesysProactiveEmailModalStore,
              private formBuilder: UntypedFormBuilder,
              private readonly actions: Actions,
              private readonly store: Store) {
  }

  ngOnInit(): void {
    this.actions.pipe(
      ofType(createEmailConversationSuccess),
      takeUntil(this.onDestroySubject)
    ).subscribe(() => {
      this.dialogRef.close();
    });

    this.actions.pipe(
      ofType(createEmailConversationFailure),
      takeUntil(this.onDestroySubject)
    ).subscribe(() => {
      this.modalStore.setLoading(false);
    });

    this.actions.pipe(
      ofType(createEmailConversationFailure),
      takeUntil(this.onDestroySubject)
    ).subscribe(() => {
      this.modalStore.setConversationFailed(true);
    });
  }

  ngOnDestroy(): void {
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }

  get recipientsRequiredError() {
    return this.recipients.errors?.required;
  }

  get queueRequiredError() {
    return this.queue.errors?.queueRequired;
  }

  addEmail(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value && GenesysProactiveEmailModalComponent.emailRegex.test(value) && !this.recipients.value.includes(value)) {

      const currentValue = this.recipients.value;
      this.recipients.setValue([
        ...currentValue,
        value
      ]);
    }

    event.chipInput?.clear();
  }

  removeEmail(email: string): void {
    const currentValue = this.recipients.value;
    this.recipients.setValue(currentValue.filter(e => e !== email));
  }

  onCreateEmailConversation(): void {
    if (this.proactiveEmailForm.valid) {
      this.modalStore.setLoading(true);
      this.modalStore.setConversationFailed(false);
      this.store.dispatch(createEmailConversation({queueId: this.queue.value?.id, toAddress: this.recipients.value.join(',')}));
    }
  }

  displayOption(queue: GenesysCloudQueue): string {
    return queue?.name || '';
  }
}
